/**
 * RynCore token-cost models. Source: the RynBridge fn app's public read
 * endpoints (PublicTokenReadRoutes allowlist, no JWT):
 *   GET /api/ryncore/tokens          -> TokenSummary
 *   GET /api/ryncore/tokens/hosts    -> TokenHostRow[]
 *   GET /api/ryncore/tokens/projects -> TokenProjectRow[]
 *
 * Shapes verified against the live endpoints 2026-08-09. Casing is
 * intentionally mixed because the API is: `daily`/`today` rows are camelCase,
 * while the period totals, by-model rows, hosts and projects are snake_case.
 * Costs are API-equivalent dollars derived from token counts, not invoices.
 */

export interface TokenModelBreakdown {
  modelName: string;
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  cost: number;
}

export interface TokenDayEntry {
  date: string;
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
  totalCost: number;
  modelsUsed: string[];
  modelBreakdowns: TokenModelBreakdown[];
}

export interface TokenPeriodTotals {
  input_tokens: number;
  output_tokens: number;
  cache_creation_tokens: number;
  cache_read_tokens: number;
  total_tokens: number;
  total_cost: number;
  cost_without_cache: number;
  days: number;
}

export interface TokenByModelRow {
  model_name: string;
  input_tokens: number;
  output_tokens: number;
  cache_creation_tokens: number;
  cache_read_tokens: number;
  cost: number;
}

export interface TokenSummary {
  daily: TokenDayEntry[];
  today: TokenDayEntry | null;
  today_cost_without_cache: number | null;
  last_7_days: TokenPeriodTotals | null;
  total: TokenPeriodTotals | null;
  by_model: TokenByModelRow[];
  generated_at: string;
  source: string;
}

export interface TokenHostRow {
  host_id: string;
  total_tokens: number;
  message_count: number;
  project_count: number;
  cost_usd: number;
  first_seen: string;
  last_seen: string;
}

export interface TokenProjectHostSlice {
  host_id: string;
  total_tokens: number;
  message_count: number;
  cost_usd: number;
}

export interface TokenProjectRow {
  project_slug: string;
  session_count: number;
  message_count: number;
  total_tokens: number;
  cost_usd: number;
  first_seen: string;
  last_seen: string;
  hosts: TokenProjectHostSlice[];
}

/** One overview card on the Cost panel (Today / Last 7 days / All time). */
export interface CostOverviewCard {
  key: 'today' | 'week' | 'total';
  label: string;
  cost: number;
  costWithoutCache: number | null;
  tokens: number;
  days: number | null;
}

/** One bar of the recent-days mini chart, scaled to the window's max cost. */
export interface DailyCostBar {
  date: string;
  cost: number;
  percentOfMax: number;
}
